import { usePageHelper } from '@affine/core/blocksuite/block-suite-page-list/utils';
import { useAsyncCallback } from '@affine/core/components/hooks/affine-async-hooks';
import { DocsService } from '@affine/core/modules/doc';
import {
  OrganizeService,
  DocTitleUniquenessService,
} from '@affine/core/modules/organize';
import { TemplateDocService } from '@affine/core/modules/template-doc';
import { WorkbenchService } from '@affine/core/modules/workbench';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { inferOpenMode } from '@affine/core/utils';
import track from '@affine/track';
import type { DocMode } from '@blocksuite/affine/model';
import { useLiveData, useService } from '@toeverything/infra';
import type { MouseEvent } from 'react';

/**
 * A common hook for creating new documents that can be used across different components
 * @return a function to create a new doc, will duplicate the template doc if the page template is enabled
 */
export const useNewDoc = () => {
  const workspaceService = useService(WorkspaceService);
  const templateDocService = useService(TemplateDocService);
  const docsService = useService(DocsService);
  const organizeService = useService(OrganizeService);
  const docTitleUniquenessService = useService(DocTitleUniquenessService);
  const workbench = useService(WorkbenchService).workbench;

  const currentWorkspace = workspaceService.workspace;
  const enablePageTemplate = useLiveData(
    templateDocService.setting.enablePageTemplate$
  );
  const pageTemplateDocId = useLiveData(
    templateDocService.setting.pageTemplateDocId$
  );

  const pageHelper = usePageHelper(currentWorkspace.docCollection);

  const createPage = useAsyncCallback(
    async (e?: MouseEvent, mode?: DocMode) => {
      let docId: string;
      if (enablePageTemplate && pageTemplateDocId) {
        docId = await docsService.duplicateFromTemplate(pageTemplateDocId);
        workbench.openDoc(`${docId}?refer=new_page`, { at: inferOpenMode(e) });
      } else {
        const page = pageHelper.createPage(mode, { at: inferOpenMode(e) });
        docId = page.id;
      }

      // yuanshi-新增：在organize中创建link
      try {
        const rootFolder = organizeService.folderTree.rootFolder;
        await docTitleUniquenessService.ensureUniqueTitleInFolder(
          docId,
          rootFolder
        );
        rootFolder.createLink(
          'doc',
          docId,
          rootFolder.indexAt('before') // 添加到最前面
        );

        // 追踪organize相关的事件
        track.$.navigationPanel.organize.createOrganizeItem({
          type: 'link',
          target: 'doc',
        });
      } catch (error) {
        console.warn('Failed to add document to organize:', error);
        // 不要让organize的错误阻止文档创建流程
      }
    },
    [
      docsService,
      enablePageTemplate,
      pageHelper,
      pageTemplateDocId,
      workbench,
      organizeService,
      docTitleUniquenessService,
    ]
  );

  return createPage;
};
